import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import './FavoriteIcon.less';
import { HasOpenState } from 'hooks/HasOpenState';
import { Callout } from 'components/layout/Callout';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { IActionLinkItem } from 'components/ActionLinks/ActionLinkModels';
import { IconButton } from 'components/buttons/IconButton';
import { FavoriteType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { getPortalSetup } from 'utils/PortalSetup';

export interface IFavoriteIconProps {
  className?: string;
  currentType: FavoriteType;
  onEdit?: () => void;
  onChange?: (type: FavoriteType) => void;
  shouldOpenMenu?: boolean;
  disableAdvanced?: boolean;
  // at the end of clean up we will get rid of this customization, i.e. 'folder' will become a single possible style for a favorite icon.
  // new - new Tree design, we going to remove this option at all
  classNamePostfix?: 'flow' | 'form' | 'entity' | 'new';
}

const getClassName = (isOpen: boolean, isFavorite: boolean, className?: string) =>
  classNames('dp-favorite-icon', className, {
    'dp-favorite-icon--active': isOpen,
    [`${className}--active`]: isFavorite && className,
  });

const getIconClassName = (active: boolean, isFavorite: boolean, classNamePostfix?: string, hasSubMenu?: boolean) => {
  let className = 'dp-favorite-icon__star';
  if (classNamePostfix?.length) {
    className += `-${classNamePostfix}`;
  }
  return classNames(className, {
    [`${className}--active`]: active,
    [`${className}--favorited`]: isFavorite,
    [`${className}--has-sub-menu`]: hasSubMenu,
  });
};

const getLinkClassName = (active: boolean) =>
  classNames('dp-favorite-icon__link', {
    'dp-favorite-icon__link--active': active,
  });

/**
 * Favorite Icon turns favorite on and off for the current user
 * if 'shouldOpenMenu' is true, clicking on icon opens a menu with ability to choose a Favorite Type
 * or open an advanced edit dialog
 **/
export function FavoriteIcon({
  className,
  currentType,
  onEdit,
  onChange,
  shouldOpenMenu,
  classNamePostfix,
  disableAdvanced,
}: IFavoriteIconProps) {
  const { isOpen, setOpen, setClosed } = HasOpenState(false);

  const ref: React.MutableRefObject<HTMLElement | any> = useRef(null);

  const onSetFavorite = useCallback(
    async (type: FavoriteType) => {
      onChange && (await onChange(type));
      setClosed();
    },
    [onChange, setClosed]
  );

  const disableEditFavorites = useMemo(() => !getPortalSetup().CanSetFavoriteForAllUsers && currentType === FavoriteType.ForAllUsers, [currentType])

  const isFavorite = currentType !== FavoriteType.None;
  const handleFavoriteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // prevent `drag and drop` event from DataDesigner
    event.stopPropagation();

    if(disableEditFavorites) {
      // toDo button should have some disable mark to show unsettable favorite
      return
    }

    if (shouldOpenMenu) {
      setOpen();
    } else {
      onSetFavorite(isFavorite ? FavoriteType.None : FavoriteType.ForMe);
    }
  };

  const getFavoriteActionslinks = () => {
    const actionLinks = [
      FavoriteType.ForMe,
      FavoriteType.ForAllUsers,
      FavoriteType.None,
    ] as FavoriteType[];

    if (!getPortalSetup().CanSetFavoriteForAllUsers) {
      const toRemove = actionLinks.indexOf(FavoriteType.ForAllUsers);
      actionLinks.splice(toRemove, 1);
    }

    let result = actionLinks.map(type => ({
      label: getFavoriteTypeString(type),
      value: type,
      onClick: () => {
        onSetFavorite(type);
      },
      className: getLinkClassName(type === currentType),
    })) as IActionLinkItem[];

    if (!disableAdvanced) {
      result = result.concat([
        {
          label: 'Advanced',
          value: FavoriteType.None,
          onClick: () => {
            setClosed();
            onEdit && onEdit();
          },
          className: 'dp-favorite-icon__link',
        },
      ]) as IActionLinkItem[];
    }
    return result;
  };

  const label = 
    disableEditFavorites ? 'Not allowed to configure' : 
    shouldOpenMenu ? 'Configure favorite' : 
      !isFavorite ? 'Add to favorites' : 'Remove from favorites';

  // click on `Favorites` popup raising blur(close) event for React Combobox
  const stopPropagationProps = useMemo(() => {
    return {
      onMouseDown: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        // cancel event bubbling for react synthetic event
        event.stopPropagation();
        // cancel event bubbling for listener from `useOnOutsideClick.ts`
        event.nativeEvent.stopImmediatePropagation();
      },
      onTouchStart: (event: React.TouchEvent) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
      },
    };
  }, []);

  return (
    <div ref={ref} className={getClassName(isOpen, isFavorite, className)} {...stopPropagationProps}>
      <IconButton
        title={label}
        aria-label={label}
        iconClassName={getIconClassName(isOpen, isFavorite, classNamePostfix, shouldOpenMenu)}
        onClick={handleFavoriteClick}
        disabled={disableEditFavorites}
      />
      <Callout
        className="dp-favorite-icon__layer"
        isOpen={isOpen}
        onClose={setClosed}
        sourceRef={ref}
        placement="bottom"
        showArrow={true}
        flipBehavior={['bottom']}
      >
        <div {...stopPropagationProps} className="dp-favorite-icon__links">
          <ActionLinks values={{ current: getFavoriteActionslinks() }} />
        </div>
      </Callout>
    </div>
  );
}

function getFavoriteTypeString(favoriteType: FavoriteType) {
  switch (favoriteType) {
    case FavoriteType.ForAllUsers: {
      return 'For All Users';
    }
    case FavoriteType.ForMe: {
      return 'For Me';
    }
    case FavoriteType.None: {
      return 'Unfavorite';
    }
  }
}
